// src/pages/ProfilePage.jsx
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Typography, 
  Row, 
  Col, 
  Button, 
  Space, 
  Form, 
  Input, 
  Select, 
  Avatar,
  Upload,
  Descriptions,
  Tag,
  Divider,
  List,
  message,
  Modal,
  Tabs,
  Badge,
  Switch,
  Alert
} from 'antd';
import { 
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  EditOutlined,
  SaveOutlined,
  LockOutlined,
  KeyOutlined,
  SettingOutlined,
  BellOutlined,
  SecurityScanOutlined,
  CloudUploadOutlined,
  CheckCircleOutlined,
  CameraOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const ProfilePage = () => {
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();

  // Simuler les données utilisateur
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  // Données mock pour le profil
  const mockUserData = {
    id: currentUser.id || 'U001',
    username: currentUser.username || 'admin',
    email: currentUser.email || 'admin@latyr.com',
    fullName: currentUser.fullName || 'Administrateur Principal',
    role: currentUser.role || 'admin',
    department: currentUser.department || 'Administration',
    phone: currentUser.phone || '+221 77 123 45 67',
    address: currentUser.address || 'Dakar, Plateau',
    joinDate: '2023-01-01',
    lastLogin: '2024-01-25 14:30:00',
    status: 'active',
    avatar: null,
    permissions: ['dashboard', 'clients', 'vehicules', 'vente', 'achat', 'fournisseurs', 'personnels', 'reports', 'settings', 'profile'],
    notifications: {
      email: true,
      push: true,
      sms: false
    },
    security: {
      twoFactor: false,
      loginNotifications: true,
      sessionTimeout: 30
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = () => {
    setLoading(true);
    // Simulation API call
    setTimeout(() => {
      setUserData(mockUserData);
      form.setFieldsValue(mockUserData);
      setLoading(false);
    }, 300);
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    if (!isEditing) {
      form.setFieldsValue(userData);
    }
  };

  const handleSave = (values) => {
    setLoading(true);
    setTimeout(() => {
      const updatedUser = { ...userData, ...values };
      setUserData(updatedUser);
      
      // Mettre à jour dans localStorage
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setIsEditing(false);
      message.success('Profil mis à jour avec succès!');
      setLoading(false);
    }, 500);
  };

  const handlePasswordChange = (values) => {
    setLoading(true);
    setTimeout(() => {
      // Simulation changement de mot de passe
      message.success('Mot de passe changé avec succès!');
      setIsPasswordModalVisible(false);
      passwordForm.resetFields();
      setLoading(false);
    }, 500);
  };

  const handleAvatarUpload = (info) => {
    if (info.file.status === 'done') {
      message.success('Photo de profil mise à jour!');
      setUserData(prev => ({
        ...prev,
        avatar: URL.createObjectURL(info.file.originFileObj)
      }));
    }
  };

  const handleNotificationToggle = (type, checked) => {
    setUserData(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [type]: checked
      }
    }));
  };

  const handleSecurityToggle = (type, checked) => {
    setUserData(prev => ({
      ...prev,
      security: {
        ...prev.security,
        [type]: checked
      }
    }));
  };

  const renderRoleBadge = (role) => {
    const roleConfig = {
      admin: { color: 'red', label: 'Administrateur', icon: <SafetyCertificateOutlined /> },
      manager: { color: 'orange', label: 'Manager', icon: <TeamOutlined /> },
      user: { color: 'blue', label: 'Utilisateur', icon: <UserOutlined /> },
      viewer: { color: 'green', label: 'Observateur', icon: <UserOutlined /> }
    }[role] || { color: 'default', label: role, icon: <UserOutlined /> };

    return (
      <Tag 
        color={roleConfig.color} 
        className="font-medium flex items-center gap-1"
      >
        {roleConfig.icon}
        {roleConfig.label}
      </Tag>
    );
  };

  if (!userData) {
    return <div className="flex justify-center items-center h-64">Chargement...</div>;
  }

  return (
    <div>
      {/* En-tête */}
      <div className="mb-6">
        <Title level={2} className="m-0">Mon Profil</Title>
        <Text className="text-gray-600 mt-2 block">
          Gérez vos informations personnelles et paramètres de compte
        </Text>
      </div>

      <Row gutter={[24, 24]}>
        {/* Colonne de gauche - Photo et informations générales */}
        <Col xs={24} lg={8}>
          <Card className="modern-card sticky top-6">
            <div className="text-center">
              <div className="relative inline-block mb-4">
                <Avatar
                  size={120}
                  src={userData.avatar}
                  icon={<UserOutlined />}
                  className="border-4 border-white shadow-lg"
                />
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  onChange={handleAvatarUpload}
                  customRequest={({ onSuccess }) => setTimeout(() => onSuccess("ok"), 1000)}
                >
                  <Button
                    type="primary"
                    shape="circle"
                    icon={<CameraOutlined />}
                    size="small"
                    className="absolute bottom-0 right-0"
                  />
                </Upload>
              </div>
              
              <Title level={3} className="mb-1">{userData.fullName}</Title>
              <Text type="secondary" className="block mb-2">@{userData.username}</Text>
              <div className="mb-4">
                {renderRoleBadge(userData.role)}
              </div>

              <Divider />

              <Descriptions column={1} size="small" className="text-left">
                <Descriptions.Item label={<><MailOutlined className="mr-2" /> Email</>}>
                  <Text>{userData.email}</Text>
                </Descriptions.Item>
                <Descriptions.Item label={<><PhoneOutlined className="mr-2" /> Téléphone</>}>
                  <Text>{userData.phone}</Text>
                </Descriptions.Item>
                <Descriptions.Item label={<><TeamOutlined className="mr-2" /> Département</>}>
                  <Text>{userData.department}</Text>
                </Descriptions.Item>
                <Descriptions.Item label={<><CalendarOutlined className="mr-2" /> Membre depuis</>}>
                  <Text>{dayjs(userData.joinDate).format('DD/MM/YYYY')}</Text>
                </Descriptions.Item>
                <Descriptions.Item label={<><CheckCircleOutlined className="mr-2" /> Dernière connexion</>}>
                  <Text>{dayjs(userData.lastLogin).format('DD/MM/YYYY HH:mm')}</Text>
                </Descriptions.Item>
              </Descriptions>

              <Divider />

              <Space direction="vertical" className="w-full">
                <Button 
                  type={isEditing ? 'default' : 'primary'} 
                  icon={isEditing ? <SaveOutlined /> : <EditOutlined />}
                  onClick={handleEditToggle}
                  block
                  loading={loading}
                >
                  {isEditing ? 'Annuler les modifications' : 'Modifier le profil'}
                </Button>
                
                <Button 
                  icon={<LockOutlined />}
                  onClick={() => setIsPasswordModalVisible(true)}
                  block
                >
                  Changer le mot de passe
                </Button>
                
                <Button 
                  icon={<SettingOutlined />}
                  onClick={() => message.info('Paramètres avancés')}
                  block
                >
                  Paramètres avancés
                </Button>
              </Space>
            </div>
          </Card>
        </Col>

        {/* Colonne de droite - Formulaire et paramètres */}
        <Col xs={24} lg={16}>
          <Tabs defaultActiveKey="personal" className="modern-card">
            <TabPane tab={<span><UserOutlined /> Informations personnelles</span>} key="personal">
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSave}
                disabled={!isEditing || loading}
              >
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      name="fullName"
                      label="Nom complet"
                      rules={[{ required: true, message: 'Veuillez saisir votre nom complet' }]}
                    >
                      <Input 
                        placeholder="Votre nom complet" 
                        prefix={<UserOutlined />}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="username"
                      label="Nom d'utilisateur"
                      rules={[{ required: true, message: 'Veuillez saisir un nom d\'utilisateur' }]}
                    >
                      <Input 
                        placeholder="Votre nom d'utilisateur" 
                        disabled
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      name="email"
                      label="Email"
                      rules={[
                        { required: true, message: 'Veuillez saisir votre email' },
                        { type: 'email', message: 'Email invalide' }
                      ]}
                    >
                      <Input 
                        placeholder="votre@email.com" 
                        type="email"
                        prefix={<MailOutlined />}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="phone"
                      label="Téléphone"
                    >
                      <Input 
                        placeholder="+221 XX XXX XX XX" 
                        prefix={<PhoneOutlined />}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      name="department"
                      label="Département"
                    >
                      <Select placeholder="Sélectionnez votre département">
                        <Option value="Administration">Administration</Option>
                        <Option value="Commercial">Commercial</Option>
                        <Option value="Logistique">Logistique</Option>
                        <Option value="Finance">Finance</Option>
                        <Option value="Technique">Technique</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="address"
                  label="Adresse"
                >
                  <Input.TextArea 
                    placeholder="Votre adresse complète" 
                    rows={2}
                    prefix={<EnvironmentOutlined />}
                  />
                </Form.Item>

                {isEditing && (
                  <div className="text-right">
                    <Button 
                      type="primary" 
                      htmlType="submit" 
                      loading={loading}
                      icon={<SaveOutlined />}
                    >
                      Enregistrer les modifications
                    </Button>
                  </div>
                )}
              </Form>
            </TabPane>

            <TabPane tab={<span><BellOutlined /> Notifications</span>} key="notifications">
              <Card size="small" className="mb-4">
                <Title level={5} className="mb-4">Préférences de notification</Title>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Text strong className="block">Notifications par email</Text>
                      <Text type="secondary" className="text-sm">
                        Recevoir les notifications importantes par email
                      </Text>
                    </div>
                    <Switch 
                      checked={userData.notifications.email}
                      onChange={(checked) => handleNotificationToggle('email', checked)}
                    />
                  </div>

                  <Divider className="my-2" />

                  <div className="flex items-center justify-between">
                    <div>
                      <Text strong className="block">Notifications push</Text>
                      <Text type="secondary" className="text-sm">
                        Recevoir des notifications sur le navigateur
                      </Text>
                    </div>
                    <Switch 
                      checked={userData.notifications.push}
                      onChange={(checked) => handleNotificationToggle('push', checked)}
                    />
                  </div>

                  <Divider className="my-2" />

                  <div className="flex items-center justify-between">
                    <div>
                      <Text strong className="block">Notifications SMS</Text>
                      <Text type="secondary" className="text-sm">
                        Recevoir des alertes par SMS
                      </Text>
                    </div>
                    <Switch 
                      checked={userData.notifications.sms}
                      onChange={(checked) => handleNotificationToggle('sms', checked)}
                    />
                  </div>
                </div>
              </Card>

              <Alert
                message="Configuration des notifications"
                description="Vous pouvez personnaliser les types de notifications que vous souhaitez recevoir pour chaque fonctionnalité."
                type="info"
                showIcon
              />
            </TabPane>

            <TabPane tab={<span><SecurityScanOutlined /> Sécurité</span>} key="security">
              <Card size="small" className="mb-4">
                <Title level={5} className="mb-4">Paramètres de sécurité</Title>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Text strong className="block">Authentification à deux facteurs</Text>
                      <Text type="secondary" className="text-sm">
                        Ajoutez une couche de sécurité supplémentaire à votre compte
                      </Text>
                    </div>
                    <Switch 
                      checked={userData.security.twoFactor}
                      onChange={(checked) => handleSecurityToggle('twoFactor', checked)}
                    />
                  </div>

                  <Divider className="my-2" />

                  <div className="flex items-center justify-between">
                    <div>
                      <Text strong className="block">Notifications de connexion</Text>
                      <Text type="secondary" className="text-sm">
                        Être notifié des nouvelles connexions à votre compte
                      </Text>
                    </div>
                    <Switch 
                      checked={userData.security.loginNotifications}
                      onChange={(checked) => handleSecurityToggle('loginNotifications', checked)}
                    />
                  </div>

                  <Divider className="my-2" />

                  <div className="flex items-center justify-between">
                    <div>
                      <Text strong className="block">Délai d'expiration de session</Text>
                      <Text type="secondary" className="text-sm">
                        {userData.security.sessionTimeout} minutes d'inactivité
                      </Text>
                    </div>
                    <Select 
                      value={userData.security.sessionTimeout.toString()} 
                      onChange={(value) => handleSecurityToggle('sessionTimeout', parseInt(value))}
                      style={{ width: 120 }}
                    >
                      <Option value="15">15 minutes</Option>
                      <Option value="30">30 minutes</Option>
                      <Option value="60">60 minutes</Option>
                      <Option value="120">2 heures</Option>
                    </Select>
                  </div>
                </div>
              </Card>

              <Card size="small" title="Historique de connexion" className="mb-4">
                <List
                  size="small"
                  dataSource={[
                    { date: '2024-01-25 14:30:00', location: 'Dakar, SN', device: 'Chrome Windows' },
                    { date: '2024-01-24 09:15:00', location: 'Dakar, SN', device: 'Firefox Windows' },
                    { date: '2024-01-23 16:45:00', location: 'Thiès, SN', device: 'Mobile iOS' },
                  ]}
                  renderItem={(item) => (
                    <List.Item>
                      <div className="w-full">
                        <div className="flex justify-between">
                          <Text strong>{dayjs(item.date).format('DD/MM/YYYY HH:mm')}</Text>
                          <Tag color="blue">{item.location}</Tag>
                        </div>
                        <Text type="secondary" className="text-xs">{item.device}</Text>
                      </div>
                    </List.Item>
                  )}
                />
              </Card>

              <Alert
                message="Sécurité du compte"
                description="Pour la sécurité de votre compte, nous vous recommandons d'activer l'authentification à deux facteurs et de changer régulièrement votre mot de passe."
                type="warning"
                showIcon
              />
            </TabPane>

            <TabPane tab={<span><KeyOutlined /> Permissions</span>} key="permissions">
              <Card size="small" className="mb-4">
                <Title level={5} className="mb-4">Vos permissions</Title>
                <div className="flex flex-wrap gap-2 mb-4">
                  {userData.permissions.map(perm => (
                    <Tag key={perm} color="blue">{perm}</Tag>
                  ))}
                </div>
                <Text type="secondary">
                  Ces permissions sont définies par votre rôle ({userData.role}). 
                  Contactez un administrateur pour modifier vos permissions.
                </Text>
              </Card>

              <Card size="small" title="Accès aux modules" className="mb-4">
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="Tableau de bord">
                    <Badge status="success" text="Accès complet" />
                  </Descriptions.Item>
                  <Descriptions.Item label="Clients">
                    <Badge status="success" text="Accès complet" />
                  </Descriptions.Item>
                  <Descriptions.Item label="Véhicules">
                    <Badge status="success" text="Accès complet" />
                  </Descriptions.Item>
                  <Descriptions.Item label="Ventes">
                    <Badge status="success" text="Accès complet" />
                  </Descriptions.Item>
                  <Descriptions.Item label="Achats">
                    <Badge status="success" text="Accès complet" />
                  </Descriptions.Item>
                  <Descriptions.Item label="Fournisseurs">
                    <Badge status="success" text="Accès complet" />
                  </Descriptions.Item>
                  <Descriptions.Item label="Personnel">
                    <Badge status="success" text="Accès complet" />
                  </Descriptions.Item>
                  <Descriptions.Item label="Rapports">
                    <Badge status="success" text="Accès complet" />
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </TabPane>
          </Tabs>
        </Col>
      </Row>

      {/* Modal pour changer le mot de passe */}
      <Modal
        title="Changer le mot de passe"
        open={isPasswordModalVisible}
        onCancel={() => {
          setIsPasswordModalVisible(false);
          passwordForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordChange}
        >
          <Form.Item
            name="currentPassword"
            label="Mot de passe actuel"
            rules={[{ required: true, message: 'Veuillez saisir votre mot de passe actuel' }]}
          >
            <Input.Password placeholder="Votre mot de passe actuel" />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="Nouveau mot de passe"
            rules={[
              { required: true, message: 'Veuillez saisir un nouveau mot de passe' },
              { min: 6, message: 'Le mot de passe doit avoir au moins 6 caractères' }
            ]}
          >
            <Input.Password placeholder="Nouveau mot de passe" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirmer le mot de passe"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Veuillez confirmer le mot de passe' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Les mots de passe ne correspondent pas'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirmer le nouveau mot de passe" />
          </Form.Item>

          <Alert
            message="Recommandations de sécurité"
            description="Utilisez un mot de passe fort avec des majuscules, minuscules, chiffres et caractères spéciaux."
            type="info"
            showIcon
            className="mb-4"
          />

          <div className="text-right">
            <Space>
              <Button onClick={() => {
                setIsPasswordModalVisible(false);
                passwordForm.resetFields();
              }}>
                Annuler
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Changer le mot de passe
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ProfilePage;